// deno-lint-ignore-file no-unused-vars
// Picker cykliczności (spec/lista-zadan.md → Ikony 🔁; Tworzenie zadań): select presetów + „Co X…”
// z liczbą i jednostką → reguła every:N:UNIT (spec/zadania.md → Zadania cykliczne).
// Wspólny dla meta row (edycja w miejscu) i formularza nowego zadania.
import { h, Fragment } from '../jsx-runtime.ts';
import { parseRecurrenceRule, formatRecurrenceRule, RECURRENCE_MAX_N, type RecurrenceUnit } from '../recurrence-core.ts';

export interface RecurrencePicker {
  el: HTMLElement;
  /** Reguła kanoniczna, null = Brak, undefined = „Co X…” z niepoprawną liczbą (nic do zapisania). */
  getValue: () => string | null | undefined;
  focus: () => void;
}

export interface RecurrencePickerOptions {
  value: string | null;
  /** Każda zmiana dająca poprawną regułę (albo null). `preset` = wybór z listy, `custom` = liczba/jednostka. */
  onChange?: (rule: string | null, source: 'preset' | 'custom') => void;
  inputClass?: string;
}

const PRESET_OPTIONS: [string, string][] = [
  ['', 'Brak'], ['daily', 'Codziennie'], ['weekly', 'Co tydzień'], ['biweekly', 'Co 2 tygodnie'],
  ['monthly', 'Co miesiąc'], ['yearly', 'Co rok'], ['custom', 'Co X…'],
];
const UNIT_OPTIONS: [RecurrenceUnit, string][] = [['day', 'dni'], ['week', 'tyg.'], ['month', 'mies.'], ['year', 'lata']];

export function renderRecurrencePicker(opts: RecurrencePickerOptions): RecurrencePicker {
  const cls = opts.inputClass ?? 'inline-edit-input';
  const el = <span className="recurrence-picker" /> as HTMLElement;
  const sel = <select className={cls + ' recurrence-preset'} title="Cykliczność" /> as HTMLSelectElement;
  for (const [v, l] of PRESET_OPTIONS) {
    const o = document.createElement('option'); o.value = v; o.textContent = l; sel.appendChild(o);
  }
  const num = <input type="number" className={cls + ' recurrence-n'} title="Co ile" /> as HTMLInputElement;
  num.min = '1'; num.max = String(RECURRENCE_MAX_N); num.step = '1';
  const unit = <select className={cls + ' recurrence-unit'} title="Jednostka" /> as HTMLSelectElement;
  for (const [v, l] of UNIT_OPTIONS) {
    const o = document.createElement('option'); o.value = v; o.textContent = l; unit.appendChild(o);
  }
  el.appendChild(sel);

  const parsed = parseRecurrenceRule(opts.value);
  const isPreset = !!opts.value && PRESET_OPTIONS.some(([v]) => v === opts.value);
  sel.value = isPreset ? opts.value! : parsed ? 'custom' : '';
  num.value = parsed ? String(parsed.n) : '2';
  unit.value = parsed ? parsed.unit : 'week';

  const showCustom = (show: boolean) => {
    if (show) { if (!num.isConnected) { el.appendChild(num); el.appendChild(unit); } }
    else { num.remove(); unit.remove(); }
  };
  showCustom(sel.value === 'custom');

  const customRule = (): string | undefined => {
    const n = Number(num.value);
    if (!Number.isInteger(n) || n < 1 || n > RECURRENCE_MAX_N) return undefined;
    return formatRecurrenceRule(n, unit.value as RecurrenceUnit);
  };
  const getValue = () => sel.value === 'custom' ? customRule() : (sel.value || null);

  sel.addEventListener('change', () => {
    showCustom(sel.value === 'custom');
    if (sel.value === 'custom') { num.focus(); num.select(); return; }   // regułę zapiszą liczba/jednostka
    opts.onChange?.(sel.value || null, 'preset');
  });
  const customChanged = () => { const r = customRule(); if (r !== undefined) opts.onChange?.(r, 'custom'); };
  num.addEventListener('change', customChanged);
  unit.addEventListener('change', customChanged);
  // Klik w kontrolki nie ma trafiać do handlerów wiersza (zaznaczanie, focus).
  el.addEventListener('click', (e: Event) => e.stopPropagation());

  return { el, getValue, focus: () => sel.focus() };
}
